import { useState } from 'react';
import { animate, useMotionValue } from 'framer-motion';

/* The simulated cursor of a staged demo: an ink dot driven by springs on
   motion values, so the beat script can `await` its travel.

   Craft notes (from the motion research):
   - springs, not beziers — successive moves blend momentum and read human;
     travel time scales with distance for free
   - x and y carry slightly different stiffness, which bows the path into a
     faint natural arc instead of a straight machine line
   - damping sits near critically-damped: a demo cursor should settle, not
     wobble
   - a click is three moments: press (with ripple), release, settle — and the
     TARGET should visibly react too (the stage handles that via hover/press
     state on the mock control). Dwell before the click belongs in the script:
     `await moveTo(...); await wait(~200); await click(wait);` */

export function useStageCursor(restX: number, restY: number) {
    const x = useMotionValue(restX);
    const y = useMotionValue(restY);
    const [press, setPress] = useState(false);
    /* Increments per click; keys the ripple span so its keyframe restarts. */
    const [ripple, setRipple] = useState(0);

    const moveTo = async (tx: number, ty: number) => {
        await Promise.all([
            animate(x, tx, {
                type: 'spring',
                stiffness: 190,
                damping: 24,
                restDelta: 0.5,
            }).finished,
            animate(y, ty, {
                type: 'spring',
                stiffness: 220,
                damping: 26,
                restDelta: 0.5,
            }).finished,
        ]);
    };

    const click = async (wait: (ms: number) => Promise<void>) => {
        setPress(true);
        setRipple((r) => r + 1);
        await wait(140);
        setPress(false);
        await wait(170);
    };

    return { x, y, press, ripple, moveTo, click };
}
